import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import SEO from '../components/SEO';
import { generateBreadcrumbSchema, generateNeighborhoodSchema } from '../utils/schema';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const NeighborhoodDetail = () => {
  const { areaSlug, neighborhoodSlug } = useParams();
  const [neighborhood, setNeighborhood] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/neighborhoods/${areaSlug}/${neighborhoodSlug}`);
        setNeighborhood(response.data);
      } catch (err) {
        console.error('Error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [areaSlug, neighborhoodSlug]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
    </div>
  );

  if (!neighborhood) return (
    <div className="container mx-auto px-4 py-16 text-center">
      <h1 className="text-2xl font-bold mb-4">Neighborhood not found</h1>
      <Link to="/service-areas" className="text-green-600 hover:underline">View all service areas</Link>
    </div>
  );

  // Only Breadcrumb and Neighborhood schemas - LocalBusiness is in index.html
  const schemas = [
    generateBreadcrumbSchema([
      { name: 'Home', url: '/' },
      { name: 'Service Areas', url: '/service-areas' },
      { name: neighborhood.area_name, url: `/service-areas/${areaSlug}` },
      { name: neighborhood.name, url: `/service-areas/${areaSlug}/${neighborhoodSlug}` }
    ]),
    generateNeighborhoodSchema(neighborhood, neighborhood.area_name, areaSlug)
  ];

  const services = [
    { name: 'Rodent Control', slug: 'rodent-control' },
    { name: 'Bed Bug Removal', slug: 'bed-bug-removal' },
    { name: 'Cockroach Extermination', slug: 'cockroach-extermination' },
    { name: 'Termite Control', slug: 'termite-control' },
    { name: 'Ant Extermination', slug: 'ant-extermination' },
    { name: 'Wasp Removal', slug: 'wasp-removal' }
  ];

  const seoKeywords = `pest control ${neighborhood.name}, exterminator ${neighborhood.name}, ${neighborhood.name} pest removal, ${neighborhood.area_name} exterminator, rat exterminator ${neighborhood.name}, bed bug removal ${neighborhood.name}, cockroach extermination ${neighborhood.name}, pest control near me ${neighborhood.name}, ${neighborhood.name} NY pest control, mice control ${neighborhood.name}`;

  return (
    <div>
      <SEO 
        title={neighborhood.meta_title}
        description={neighborhood.meta_description}
        keywords={seoKeywords}
        schema={schemas}
        canonical={`https://www.reclaimpestcontrol.com/service-areas/${areaSlug}/${neighborhoodSlug}`}
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-700 to-green-600 text-white py-16">
        <div className="container mx-auto px-4">
          <nav className="text-sm mb-4 text-white/80">
            <Link to="/" className="hover:text-white">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/service-areas" className="hover:text-white">Service Areas</Link>
            <span className="mx-2">/</span>
            <Link to={`/service-areas/${areaSlug}`} className="hover:text-white">{neighborhood.area_name}</Link>
            <span className="mx-2">/</span>
            <span className="text-white">{neighborhood.name}</span>
          </nav>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Pest Control in {neighborhood.name}, {neighborhood.area_name}
          </h1>
          <p className="text-xl max-w-3xl text-white/90">
            Professional exterminator services in {neighborhood.name}. Fast, reliable pest control for homes and businesses. 24/7 emergency service available.
          </p>
          {neighborhood.zip_codes && neighborhood.zip_codes.length > 0 && (
            <p className="mt-4 text-white/80 text-sm">
              Serving ZIP codes: {neighborhood.zip_codes.join(', ')}
            </p>
          )}
          <div className="mt-6 flex flex-wrap gap-4">
            <a 
              href="tel:6462099955" 
              className="bg-white text-green-600 px-6 py-3 rounded-lg font-bold hover:bg-gray-100 transition flex items-center"
              data-testid="hero-call-btn"
            >
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20"><path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"/></svg>
              Call (646) 209-9955
            </a>
            <Link 
              to="/contact" 
              className="bg-blue-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-blue-700 transition"
              data-testid="hero-quote-btn"
            >
              Get Free Quote
            </Link>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Left Column - Main Content */}
            <div className="lg:col-span-2 space-y-12">
              {/* About Section */}
              <div>
                <h2 className="text-3xl font-bold mb-6">Expert Pest Control in {neighborhood.name}</h2>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Reclaim Pest Control provides comprehensive pest management services to residents and businesses in {neighborhood.name}, {neighborhood.area_name}. Our licensed exterminators are experts at addressing the specific pest challenges in your neighborhood, from rodents and bed bugs to cockroaches and termites.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Whether you&apos;re dealing with a sudden infestation or looking for preventive pest control, our team delivers fast, effective solutions with guaranteed results. We understand the unique pest pressures in {neighborhood.name} and use targeted treatments that are safe for your family and pets.
                </p>
              </div>

              {/* Services in This Area */}
              <div>
                <h2 className="text-2xl font-bold mb-6">Pest Control Services in {neighborhood.name}</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  {services.map(service => (
                    <Link 
                      key={service.slug}
                      to={`/services/${service.slug}`}
                      className="flex items-center p-4 bg-white border rounded-lg hover:border-green-500 hover:shadow-md transition group"
                      data-testid={`service-link-${service.slug}`}
                    >
                      <div className="flex-1">
                        <h3 className="font-bold group-hover:text-green-600 transition">{service.name}</h3>
                        <p className="text-sm text-gray-600">{service.name} in {neighborhood.name}</p>
                      </div>
                      <svg className="w-5 h-5 text-gray-400 group-hover:text-green-600 transition" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                    </Link>
                  ))}
                </div>
                <div className="mt-6 text-center">
                  <Link to="/services" className="text-green-600 font-medium hover:underline">View All Services →</Link>
                </div>
              </div>

              {/* Why Choose Us */}
              <div className="bg-gray-50 p-8 rounded-lg">
                <h2 className="text-2xl font-bold mb-6">Why {neighborhood.name} Residents Choose Us</h2>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="flex items-start">
                    <span className="bg-green-100 text-green-600 p-2 rounded-lg mr-4">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                    </span>
                    <div>
                      <h3 className="font-bold mb-1">Local {neighborhood.name} Experts</h3>
                      <p className="text-gray-600 text-sm">We know your neighborhood and its pest challenges</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <span className="bg-green-100 text-green-600 p-2 rounded-lg mr-4">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    </span>
                    <div>
                      <h3 className="font-bold mb-1">Fast Response Time</h3>
                      <p className="text-gray-600 text-sm">Same-day service available in {neighborhood.name}</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <span className="bg-green-100 text-green-600 p-2 rounded-lg mr-4">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
                    </span>
                    <div>
                      <h3 className="font-bold mb-1">Licensed & Insured</h3>
                      <p className="text-gray-600 text-sm">Fully certified pest control professionals</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <span className="bg-green-100 text-green-600 p-2 rounded-lg mr-4">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    </span>
                    <div>
                      <h3 className="font-bold mb-1">Satisfaction Guaranteed</h3>
                      <p className="text-gray-600 text-sm">We stand behind our work 100%</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                {/* Contact CTA */}
                <div className="bg-green-600 text-white p-6 rounded-lg">
                  <h3 className="text-xl font-bold mb-3">Get Pest-Free in {neighborhood.name}!</h3>
                  <p className="text-white/90 mb-4 text-sm">Fast response, guaranteed results.</p>
                  <a 
                    href="tel:6462099955" 
                    className="block bg-white text-green-600 text-center py-3 rounded-lg font-bold mb-3 hover:bg-gray-100 transition"
                  >
                    (646) 209-9955
                  </a>
                  <Link 
                    to="/contact" 
                    className="block bg-blue-600 text-center py-3 rounded-lg font-bold hover:bg-blue-700 transition"
                  >
                    Request Free Quote
                  </Link>
                </div>

                {/* Back to Area */}
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="font-bold mb-4">More in {neighborhood.area_name}</h3>
                  <Link 
                    to={`/service-areas/${areaSlug}`}
                    className="text-green-600 hover:underline flex items-center"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                    View all {neighborhood.area_name} neighborhoods
                  </Link>
                </div>

                {/* Trust Badges */}
                <div className="bg-white border rounded-lg p-6">
                  <h3 className="font-bold mb-4 text-center">Why Trust Us</h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex items-center">
                      <span className="text-green-500 mr-2">✓</span>
                      Licensed & Insured
                    </div>
                    <div className="flex items-center">
                      <span className="text-green-500 mr-2">✓</span>
                      Eco-Friendly Options
                    </div>
                    <div className="flex items-center">
                      <span className="text-green-500 mr-2">✓</span>
                      24/7 Emergency Service
                    </div>
                    <div className="flex items-center">
                      <span className="text-green-500 mr-2">✓</span>
                      Satisfaction Guaranteed
                    </div>
                    <div className="flex items-center">
                      <span className="text-green-500 mr-2">✓</span>
                      Free Inspections
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-blue-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready for Pest-Free Living in {neighborhood.name}?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto text-white/90">
            Contact Reclaim Pest Control today for professional pest control services in {neighborhood.name}, {neighborhood.area_name}.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:6462099955" 
              className="bg-green-500 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-green-600 transition"
            >
              Call (646) 209-9955
            </a>
            <Link 
              to="/contact" 
              className="bg-white text-blue-900 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition"
            >
              Schedule Free Inspection
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NeighborhoodDetail;
